import { FC, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getContentById, updateContent, Content, DocumentData } from '@/services/university-service';
import { Spinner, Center, VStack, useToast, Button, HStack, Text, Icon, FormControl, FormErrorMessage, FormLabel, Input, NumberDecrementStepper, NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper, Textarea } from '@chakra-ui/react';
import { MdSave, MdArrowBack } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { ADMIN_ROUTES } from '@bemap/utils';
import { useForm } from 'react-hook-form';
import { Panel, PageHeading } from '@bemap/ui';

interface DocumentContentFormData {
  title: string;
  content: string;
  order: number;
  duration: number;
}

const EditDocumentContentPage: FC = () => {
  const { courseId, contentId } = useParams<{ courseId: string; moduleId: string; contentId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [content, setContent] = useState<Content | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = useForm<DocumentContentFormData>({
    defaultValues: {
      title: '',
      content: '',
      order: 1,
      duration: 15
    }
  });

  // Load content data
  useEffect(() => {
    const loadContent = async () => {
      if (!contentId) return;

      try {
        setIsLoading(true);
        const response = await getContentById(parseInt(contentId));

        if (response.statusCode === 200 && response.message) {
          const contentData = response.message as Content;
          setContent(contentData);

          // Populate form with content data
          reset({
            title: contentData.title,
            content: (contentData.data as DocumentData)?.markdown || '',
            order: contentData.order,
            duration: contentData.duration ? parseInt(contentData.duration) : 15
          });
        } else {
          throw new Error(response.message || t('university.content.content_not_found'));
        }
      } catch (error: any) {
        console.error('Error loading content:', error);
        toast({
          title: t('university.content.error_loading_content'),
          description: error.message || t('general.error'),
          status: 'error',
          duration: 30,
          isClosable: true,
        });
        navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, [contentId, navigate, toast, reset, t, courseId]);

  const handleBack = () => {
    navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
  };

  const onSubmit = async (data: DocumentContentFormData) => {
    if (!contentId) return;

    setIsSubmitting(true);

    try {
      const contentData: Partial<Content> = {
        title: data.title,
        data: {
          markdown: data.content
        },
        order: data.order,
        duration: data.duration.toString()
      };

      const response = await updateContent(parseInt(contentId), contentData);

      if (response.statusCode === 200) {
        toast({
          title: t('university.content.document_updated'),
          description: `${t('university.content.edit_document')}${data.title}" ${t('university.content.document_updated')}`,
          status: 'success',
          duration: 30,
          isClosable: true,
        });

        navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
      } else {
        throw new Error(response.message || t('university.content.error_updating_document'));
      }
    } catch (error: any) {
      console.error('Error updating document content:', error);
      toast({
        title: t('university.content.error_updating_document'),
        description: error.message || t('general.error'),
        status: 'error',
        duration: 30,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>{t('general.loading')}</Text>
        </VStack>
      </Center>
    );
  }

  if (!content) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Text>{t('university.content.content_not_found')}</Text>
          <Button onClick={handleBack}>{t('university.content.back_to_course')}</Button>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header */}
      <PageHeading title={`${t('university.content.edit_document')}: ${content.title}`} actions={[
        {
          label: t('university.content.back_to_course'),
          onClick: handleBack,
          icon: <Icon as={MdArrowBack} />,
          variant: 'outline'
        }
      ]} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack align="stretch" spacing={6}>
          {/* Document Content Form */}
          <Panel heading={t('university.content.document_information')} padding={6}>
            <VStack spacing={4}>
              <FormControl isInvalid={!!errors.title} isRequired>
                <FormLabel>{t('university.content.title')}</FormLabel>
                <Input
                  {...register('title', {
                    required: t('university.validation.title_required'),
                    minLength: { value: 3, message: t('university.validation.title_min_length') }
                  })}
                  placeholder={t('university.placeholders.document_title')}
                />
                <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.content} isRequired>
                <FormLabel>{t('university.content.content')}</FormLabel>
                <Textarea
                  {...register('content', {
                    required: t('university.validation.content_required'),
                    minLength: { value: 10, message: t('university.validation.content_min_length') }
                  })}
                  placeholder={t('university.placeholders.document_content')}
                  rows={12}
                  resize="vertical"
                />
                <FormErrorMessage>{errors.content?.message}</FormErrorMessage>
              </FormControl>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>{t('university.content.order')}</FormLabel>
                  <NumberInput min={1} defaultValue={1}>
                    <NumberInputField {...register('order', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>{t('university.content.estimated_duration')}</FormLabel>
                  <NumberInput min={1} defaultValue={15}>
                    <NumberInputField {...register('duration', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    {t('university.content.minutes')}
                  </Text>
                </FormControl>
              </HStack>
            </VStack>
          </Panel>

          {/* Action Buttons */}
          <HStack justify="space-between">
            <Button
              variant="outline"
              onClick={handleBack}
              isDisabled={isSubmitting}
            >
              {t('actions.cancel')}
            </Button>

            <HStack spacing={3}>
              <Button
                type="submit"
                colorScheme="blue"
                leftIcon={<Icon as={MdSave} />}
                isLoading={isSubmitting}
                loadingText={t('general.saving')}
                isDisabled={!isValid}
              >
                {t('university.content.update_document')}
              </Button>
            </HStack>
          </HStack>
        </VStack>
      </form>
    </VStack>
  );
};

export default EditDocumentContentPage; 